<template>
  <div v-if="app" class="product-app">
    <BreadCrumb class="text-white" />
    <div class="hero page">
      <div class="container">
        <h1 class="main-title">{{ app?.headings.h1[0] }}</h1>
        <h3 class="main-subtitle">
          {{ app?.headings.h3[0] }}
        </h3>
        <div class="hero-content">
          <div class="img-wrapper">
            <img
              :src="appImage.url"
              :alt="appImage.alt"
              :title="appImage.title"
              loading="lazy"
            />
          </div>
          <div class="text-wrapper">
            <p>{{ t("page-app_carista-app-description-1") }}</p>
            <p>{{ t("page-app_carista-app-description-2") }}</p>
            <p class="store-title">{{ t("page-app_available-on") }}</p>
            <div
              class="store-container"
            >
              <a
                :href="$appConfig.appleAppUrl"
                target="_blank"
                class="btn-store-outlined"
                @click="facebookGoogleAnalitycs('app_store_click', {
                  button_position: 'app page',
                  platform: 'App Store'
                })"
              >
                <AppStoreIcon />
              </a>
              <a
                :href="$appConfig.googleAppUrl"
                target="_blank"
                class="btn-store-outlined"
                @click="facebookGoogleAnalitycs('app_store_click', {
                  button_position: 'app page',
                  platform: 'Google Play'
                })"
              >
                <GoogePlayIcon />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
    <HomePageTools :content="toolsContent" :active="2" />
    <div class="why-app">
      <div class="container">
        <h2>{{ app?.headings.h2[0] }}</h2>
        <div class="content">
          <div class="img-wrapper hide-on-mobile">
            <img
              :src="appImage.url"
              :alt="appImage.alt"
              :title="appImage.title"
              loading="lazy"
            />
          </div>
          <div class="text">
            <p>
              {{ t("page-app_experience-our-interface-gradient-1") }}
              <span
                class="text-gradient"
                >{{ t("page-app_experience-our-interface-gradient-2") }}</span
              >
              <br class="hide-on-mobile" />
              {{ t("page-app_experience-our-interface-gradient-3") }}
            </p>
            <p>
              {{ t("page-app_save-more-than-700-gradient-1") }}
              <span
                class="text-gradient"
                >{{ t("page-app_save-more-than-700-gradient-2") }}</span
              >
              {{ t("page-app_save-more-than-700-gradient-3") }}
              <br class="hide-on-mobile" />
              {{ t("page-app_save-more-than-700-gradient-4") }}
            </p>
            <p>
              {{
                t(
                  "page-app_resolve-inquiries-with-the-help-of-our-team-gradient-1"
                )
              }}
              <br class="hide-on-mobile" />
              {{
                t(
                  "page-app_resolve-inquiries-with-the-help-of-our-team-gradient-2"
                )
              }}
              <span
                class="text-gradient"
              >
                {{
                  t(
                    "page-app_resolve-inquiries-with-the-help-of-our-team-gradient-3"
                  )
                }}
                {{
                  t(
                    "page-app_resolve-inquiries-with-the-help-of-our-team-gradient-4"
                  )
                }}
              </span>
            </p>
            <p v-html="whyAppSubText" :style="{width: 400}"></p>
          </div>
        </div>
      </div>
    </div>
    <SupportedBrands />
    <HowItWorks
      id="howItWorksSection"
      :content="howItWorksContent"
      :active="active"
      :activeStep="activeStep"
      @changeActiveStep="changeActiveStep"
    />
    <MobileAppAndAdapter id="bottomSection" />
    <VueperSlider
      :title="app?.headings.h4[0]"
      :testimonials="appTestimonials"
      :active="6"
      :static="1"
      ref="testimonials"
    />
    <Faq :active="7" :static="true" :faqList="appFaq" :title="app?.headings.h4[1]"/>
    <PromoSection
      fbEventParam="app page"
    />
  </div>
</template>

<script setup>
import { computed, ref, onMounted, watch, onBeforeMount, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import BreadCrumb from "@/components/breadcrumb/BreadCrumb";
import HowItWorks from "@/components/HowItWorks";
import SupportedBrands from "@/components/adapter/SupportedBrands";
import MobileAppAndAdapter from "@/components/adapter/MobileAppAndAdapter";
import HomePageTools from "@/components/homepage/HomePageTools";
import AppStoreIcon from "@/assets/icons/AppStoreIcon";
import GoogePlayIcon from "@/assets/icons/GoogePlayIcon";
import VueperSlider from "@/components/VueperSlider";
import Faq from "@/components/Faq";
import {
  facebookGoogleAnalitycs,
  createMetaInfo,
  mapLangFullNameToCode,
  formatPhraseWithHtml,
  findByTag
} from "@/utils/helpers";
import { useTranslation } from "i18next-vue";
import { useHead } from '@unhead/vue';
import { TESTIMONIALS_CATEGORY_ID, FAQ_CATEGORY_ID } from '@/utils/constants';
import PromoSection from '@/components/PromoSection.vue';
import { useScrollAnimation } from '@/composables/useScrollAnimation';

const store = useStore();
const { active, activeStep } = useScrollAnimation();

const appFaqCategory = FAQ_CATEGORY_ID.APP_PAGE;
const appFaq = computed(() => store.state.faq?.faq[appFaqCategory - 1]);
const faqLinkedData = computed(() => store.state.faq.linkedData[appFaqCategory - 1]);
const appTestimonialsCategory = TESTIMONIALS_CATEGORY_ID.APP_PAGE;
const appTestimonials = computed(
  () => store.state.testimonials?.testimonials[appTestimonialsCategory - 1]
);
const app = computed(() => store.state.pages?.app);
const howItWorksContent = computed(() => app.value.items?.find(
  (item) => item.tag == 'how-it-works' || {}
));
const toolsContent = computed(() => app.value?.items.find(
  (item) => item.tag == 'tools'
));
const langCode = computed(() => store.state.language.lang?.code);
const { t } = useTranslation(['page-app', 'shared']);

const testimonials = ref(null);

const appImage = computed(() => {
  const image = findByTag(app.value?.images, 'phone-main-screen-hero-section-carista-app');

  return {
    url: image?.url,
    alt: image?.imageMeta?.metaAltDescription,
    title: image?.imageMeta?.metaTitle,
}});

const whyAppSubText = computed(() => {
  const text = t('page-app_perform-coding-diagnostics-and-service-quicly-and-reliably');
  const gradient = t('page-app_perform-coding-diagnostics-and-service-quicly-and-reliably-gradient');

  const className = 'text-gradient';

  return formatPhraseWithHtml(
        text,
        gradient,
        'span',
        className
    );
})

const changeActiveStep = ($event) => {
  activeStep.value = $event;
};

onBeforeMount(async () => {
  if (!store.state.pages.app) {
    store.dispatch('pages/getPageByTag', { tag: 'app' });
  }

  if (!store.state.pages.pricing) {
   await store.dispatch('pages/getPageByTag', { tag: 'pricing' });
  }

  if (
    !appTestimonials.value ||
    (appTestimonials.value &&
      mapLangFullNameToCode(appTestimonials?.value[0].language) !== langCode.value)
  ) {
    await store.dispatch(
      "testimonials/getTestimonials", { lang: langCode.value, categoryId: appTestimonialsCategory }
    );
  }

  if (
    !appFaq.value ||
    (appFaq.value && mapLangFullNameToCode(appFaq?.value[0].language) !== langCode.value)
  ) {
    await store.dispatch("faq/getFaq", { lang: langCode.value, categoryId: appFaqCategory });
  }

  if (!store.state.vehiclebrands.brands.length) {
    await store.dispatch("vehiclebrands/getBrands");
  }
});

onMounted(() => {
  document.body.classList.remove("overflow-hidden");
});

watch(langCode, (newVal) => {
  store.dispatch(
    "testimonials/getTestimonials", { lang: newVal, categoryId: appTestimonialsCategory }
  );
  store.dispatch("faq/getFaq", { lang: newVal, categoryId: appFaqCategory });
});

const headControl = useHead(() => {
  return createMetaInfo({
    title: app.value?.metaTitle,
    pageMeta: app.value,
  });
});

watch(faqLinkedData, () => {
    const baseMetaInfo = createMetaInfo({
    title: app.value?.metaTitle,
    pageMeta: app.value,
  });

  const headConfig = {
    ...baseMetaInfo,
    script: [{
      key: 'faq-structured-data',
      type: 'application/ld+json',
      textContent: faqLinkedData.value,
    }]
  };

  headControl.patch({
    ...headConfig
  });
});

</script>
